import * as React from "react";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import TireSearch from "./TireSearch";
import { getBanners } from "../../../services/core-api";
import { ReadBanner } from "../../../interfaces/core/ReadBanner";

const DEFAULT_HERO_IMAGE = "./banner1.jpg";
const BANNER_ROTATE_MS = 6000;

const resolveBannerImage = (banner: ReadBanner): string => {
  const baseURL = process.env.REACT_APP_API_URL;
  if (banner.image_url) return banner.image_url;
  if (
    banner.image &&
    (banner.image.startsWith("http://") || banner.image.startsWith("https://"))
  ) {
    return banner.image;
  }
  if (banner.image) return `${baseURL}/${banner.image}`;
  return DEFAULT_HERO_IMAGE;
};

/** Full-width hero: banner background with centered tire search wizard. */
const TireVehiclePanel = () => {
  const [banners, setBanners] = React.useState<ReadBanner[]>([]);
  const [activeBannerIndex, setActiveBannerIndex] = React.useState(0);

  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getBanners();
        if (active && data?.length) {
          setBanners(data);
        }
      } catch {
        // Fall back to local placeholder image when no banners are configured.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (banners.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActiveBannerIndex((prev) => (prev + 1) % banners.length);
    }, BANNER_ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [banners.length]);

  const heroImage =
    banners.length > 0
      ? resolveBannerImage(banners[activeBannerIndex])
      : DEFAULT_HERO_IMAGE;

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: { xs: 520, sm: 460, md: 420 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${heroImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transition: "background-image 0.6s ease-in-out",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.55)",
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: 1200,
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 5, md: 6 },
            textAlign: "center",
          }}
        >
          <Typography
            component="h1"
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            Find Tires For Your Vehicle
          </Typography>
          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: { xs: "0.95rem", md: "1.05rem" },
              mb: { xs: 3, md: 4 },
            }}
          >
            Search by make, model, year and modification
          </Typography>

          <TireSearch hero />

          {banners.length > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 1,
                mt: 3,
              }}
            >
              {banners.map((banner, index) => (
                <Box
                  key={banner.id}
                  onClick={() => setActiveBannerIndex(index)}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    cursor: "pointer",
                    backgroundColor:
                      index === activeBannerIndex
                        ? "secondary.main"
                        : "rgba(255, 255, 255, 0.45)",
                    transition: "background-color 0.2s ease",
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TireVehiclePanel;
